import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Eye,
  LayoutDashboard, ScanLine, CandlestickChart, ClipboardList, FolderOpen,
  Brain, CalendarDays, ShieldCheck, Beaker, BookOpen, TrendingUp, Bell,
  Settings as SettingsIcon, Moon, Sun, ChevronLeft, KeyRound, X,
} from "lucide-react";

const NAV = [
  { to: "/watchlist", label: "Watchlist", icon: Eye },
  { to: "/", label: "Pusat Komando", icon: LayoutDashboard, end: true },
  { to: "/scanner", label: "Pemindai Pasar", icon: ScanLine },
  { to: "/chart", label: "Analisis Grafik", icon: CandlestickChart },
  { to: "/planner", label: "Perencana Trading", icon: ClipboardList },
  { to: "/plans", label: "Rencana Aktif", icon: FolderOpen },
  { to: "/intelligence", label: "Intelijen Pasar", icon: Brain },
  { to: "/calendar", label: "Kalender Ekonomi", icon: CalendarDays },
  { to: "/risk", label: "Pusat Risiko", icon: ShieldCheck },
  { to: "/paper", label: "Trading Simulasi", icon: Beaker },
  { to: "/journal", label: "Jurnal Trading", icon: BookOpen },
  { to: "/performance", label: "Performa", icon: TrendingUp },
  { to: "/alerts", label: "Peringatan", icon: Bell },
  { to: "/access", label: "Kontrol Akses", icon: KeyRound },
  { to: "/settings", label: "Pengaturan", icon: SettingsIcon },
];

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const [dark, setDark] = useState(true);
  const hideLabel = collapsed ? "md:hidden" : "";

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" onClick={() => setMobileOpen(false)} data-testid="sidebar-backdrop" />
      )}
      <aside
        data-testid="app-sidebar"
        className={`fixed left-0 top-0 h-screen z-50 flex flex-col border-r transition-transform duration-200 w-[236px] ${
          collapsed ? "md:w-[68px]" : "md:w-[236px]"
        } ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0`}
        style={{
          background: "linear-gradient(180deg,#0c0c0f 0%,#0a0a0c 100%)",
          borderColor: "var(--border)",
        }}
      >
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 h-16 shrink-0">
        <div
          className="grid place-items-center rounded-xl shrink-0"
          style={{ width: 34, height: 34, background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", boxShadow: "0 6px 18px -6px rgba(139,92,246,.6)" }}
        >
          <CandlestickChart size={18} color="#fff" strokeWidth={2.4} />
        </div>
        <div className={`leading-tight ${hideLabel}`}>
          <div className="text-[13px] font-extrabold tracking-wide">YUMAERA</div>
          <div className="text-[9px] font-semibold tracking-[0.22em]" style={{ color: "var(--accent-soft)" }}>TRADEPLAN</div>
        </div>
        {/* Mobile close */}
        <button onClick={() => setMobileOpen(false)} data-testid="sidebar-close"
          className="ml-auto md:hidden grid place-items-center rounded-lg" style={{ width: 32, height: 32, background: "var(--panel-2)", border: "1px solid var(--border)" }}>
          <X size={16} style={{ color: "var(--text-muted)" }} />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto px-2.5 py-2 space-y-0.5">
        <div className={`px-2 pt-2 pb-1 text-[10px] font-semibold tracking-widest ${hideLabel}`} style={{ color: "var(--text-dim)" }}>WORKSPACE</div>
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-lg px-2.5 py-2 text-[13px] font-medium ym-hover ${
                  isActive ? "text-white" : ""
                }`
              }
              style={({ isActive }) => ({
                color: isActive ? "#fff" : "var(--text-muted)",
                background: isActive ? "linear-gradient(90deg,rgba(139,92,246,.18),rgba(139,92,246,.04))" : "transparent",
                border: isActive ? "1px solid rgba(139,92,246,.35)" : "1px solid transparent",
              })}
            >
              <Icon size={17} className="shrink-0" />
              <span className={`truncate ${hideLabel}`}>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Dark mode + collapse */}
      <div className="shrink-0 px-3 py-3 border-t" style={{ borderColor: "var(--border)" }}>
        <div className="flex items-center justify-between gap-2">
          <span className={`text-[12px] ${hideLabel}`} style={{ color: "var(--text-muted)" }}>Dark mode</span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setDark((d) => !d)}
              className="grid place-items-center rounded-full ym-hover"
              style={{ width: 32, height: 32, background: "var(--panel-2)", border: "1px solid var(--border)" }}
              title="Toggle theme"
            >
              {dark ? <Moon size={15} color="var(--accent-soft)" /> : <Sun size={15} color="var(--amber)" />}
            </button>
            <button
              onClick={() => setCollapsed((c) => !c)}
              className="hidden md:grid place-items-center rounded-full ym-hover"
              style={{ width: 32, height: 32, background: "var(--panel-2)", border: "1px solid var(--border)", transform: collapsed ? "rotate(180deg)" : "none" }}
              title="Collapse"
            >
              <ChevronLeft size={15} color="var(--text-muted)" />
            </button>
          </div>
        </div>
      </div>
      </aside>
    </>
  );
}
